import { useState, useMemo } from "react";
import { motion } from "framer-motion";
import { Sliders, TrendingUp, AlertTriangle, CheckCircle2, RefreshCw } from "lucide-react";
import { TIER_COLORS, getTier, indicativeLimit } from "@/lib/tiers";


/**
 * LenderSimulator — "What-if" sidebar for lenders to tweak score parameters.
 * Allows adjusting signal weights and seeing projected score changes.
 */
export default function LenderSimulator({ baseScore, signals, className = "" }) {
  const [adjustments, setAdjustments] = useState({
    academic: 0,
    fee_payment: 0,
    developer_professional: 0,
    social_creator: 0,
    coverage_consistency: 0,
  });

  const [riskTolerance, setRiskTolerance] = useState(50); // 0-100 slider
  const [creditLimit, setCreditLimit] = useState(25000); // suggested credit limit

  const SIGNAL_LABELS = {
    academic: "Academic",
    fee_payment: "Fee Payment",
    developer_professional: "Developer",
    social_creator: "Social",
    coverage_consistency: "Coverage",
  };

  const SIGNAL_COLORS = {
    academic: "#3b82f6",
    fee_payment: "#06b6d4",
    developer_professional: "#8b5cf6",
    social_creator: "#f59e0b",
    coverage_consistency: "#10b981",
  };

  const simulatedScore = useMemo(() => {
    if (!baseScore || !signals) return baseScore || 0;

    const weights = { academic: 0.30, fee_payment: 0.25, developer_professional: 0.20, social_creator: 0.15, coverage_consistency: 0.10 };
    let totalDelta = 0;

    Object.entries(adjustments).forEach(([key, adj]) => {
      const weight = weights[key] || 0;
      totalDelta += adj * weight * 6; // 6 = score sensitivity factor
    });

    // Risk tolerance affects score interpretation
    const riskFactor = 1 + (riskTolerance - 50) / 200; // 0.75 to 1.25
    const adjusted = Math.round((baseScore + totalDelta) * riskFactor);
    return Math.max(300, Math.min(900, adjusted));
  }, [baseScore, adjustments, riskTolerance, signals]);

  const suggestedLimit = useMemo(() => indicativeLimit(simulatedScore), [simulatedScore]);

  const tier = getTier(simulatedScore);
  const tierColor = TIER_COLORS[tier];
  const delta = simulatedScore - (baseScore || 0);

  const reset = () => {
    setAdjustments({ academic: 0, fee_payment: 0, developer_professional: 0, social_creator: 0, coverage_consistency: 0 });
    setRiskTolerance(50);
  };

  return (
    <div className={`w-full min-w-0 bg-white rounded-xl border border-slate-200 shadow-sm ${className}`}>
      <div className="p-4 border-b border-slate-100 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Sliders size={16} className="text-blue-600" />
          <h3 className="text-sm font-semibold text-slate-700">Lender Simulator</h3>
        </div>
        <button onClick={reset} className="text-xs text-slate-400 hover:text-slate-600 transition-colors inline-flex items-center gap-1">
          <RefreshCw size={12} /> Reset
        </button>
      </div>

      <div className="p-4 space-y-4">
        {/* Simulated Score */}
        <div className="text-center p-4 rounded-lg" style={{ backgroundColor: `${tierColor}10`, border: `1px solid ${tierColor}30` }}>
          <p className="text-xs text-slate-500 mb-1">Simulated Score</p>
          <motion.p
            key={simulatedScore}
            initial={{ scale: 1.1 }}
            animate={{ scale: 1 }}
            className="font-mono text-3xl font-bold"
            style={{ color: tierColor }}
          >
            {simulatedScore}
          </motion.p>
          <div className="flex items-center justify-center gap-2 mt-1">
            <span className="text-xs font-semibold px-2 py-0.5 rounded-full" style={{ backgroundColor: `${tierColor}20`, color: tierColor }}>
              {tier}
            </span>
            {delta !== 0 && (
              <span className={`text-xs font-mono font-bold ${delta > 0 ? "text-green-600" : "text-red-500"}`}>
                {delta > 0 ? "+" : ""}{delta}
              </span>
            )}
          </div>
        </div>

        {/* Signal Adjustments */}
        <div className="space-y-3">
          <p className="text-xs text-slate-500 font-semibold uppercase tracking-wide">Adjust Signal Scores</p>
          {Object.entries(adjustments).map(([key, val]) => {
            const signalScore = signals?.[key]?.score || 0;
            return (
              <div key={key}>
                <div className="flex justify-between text-xs mb-1">
                  <span className="text-slate-600">{SIGNAL_LABELS[key]}</span>
                  <span className="font-mono text-slate-400">
                    {signalScore}{val !== 0 ? ` ${val > 0 ? "+" : ""}${val}` : ""}
                  </span>
                </div>
                <input
                  type="range"
                  min={-20}
                  max={20}
                  value={val}
                  onChange={(e) => setAdjustments(prev => ({ ...prev, [key]: parseInt(e.target.value) }))}
                  className="w-full h-1.5 rounded-full appearance-none cursor-pointer"
                  style={{
                    background: `linear-gradient(to right, #ef4444 0%, #94a3b8 50%, ${SIGNAL_COLORS[key]} 100%)`,
                  }}
                />
              </div>
            );
          })}
        </div>

        {/* Risk Tolerance */}
        <div>
          <div className="flex justify-between text-xs mb-1">
            <span className="text-slate-600">Risk Tolerance</span>
            <span className="font-mono text-slate-400">{riskTolerance}%</span>
          </div>
          <input
            type="range"
            min={0}
            max={100}
            value={riskTolerance}
            onChange={(e) => setRiskTolerance(parseInt(e.target.value))}
            className="w-full h-1.5 rounded-full appearance-none cursor-pointer"
            style={{
              background: `linear-gradient(to right, #22c55e 0%, #f59e0b 50%, #ef4444 100%)`,
            }}
          />
          <div className="flex justify-between text-[10px] text-slate-400 mt-0.5">
            <span>Conservative</span>
            <span>Aggressive</span>
          </div>
        </div>

        {/* Suggested Credit Limit */}
        <div className="p-3 bg-blue-50 rounded-lg border border-blue-100">
          <p className="text-xs text-blue-600 font-semibold mb-1">Suggested Credit Limit</p>
          <p className="font-mono text-xl font-bold text-blue-700">
            ₹{suggestedLimit.toLocaleString("en-IN")}
          </p>
          <p className="text-[10px] text-blue-500 mt-1">
            Based on simulated score of {simulatedScore} ({tier} tier) with {riskTolerance}% risk tolerance
          </p>
        </div>

        {/* Risk Assessment */}
        <div className="space-y-2">
          <p className="text-xs text-slate-500 font-semibold uppercase tracking-wide">Quick Assessment</p>
          {simulatedScore >= 750 ? (
            <div className="flex items-start gap-2 p-2 rounded-lg bg-green-50">
              <CheckCircle2 size={14} className="text-green-600 shrink-0 mt-0.5" />
              <p className="text-xs text-green-700">Strong candidate. Recommend standard approval with favorable terms.</p>
            </div>
          ) : simulatedScore >= 600 ? (
            <div className="flex items-start gap-2 p-2 rounded-lg bg-amber-50">
              <TrendingUp size={14} className="text-amber-600 shrink-0 mt-0.5" />
              <p className="text-xs text-amber-700">Moderate risk. Consider conditional approval with usage monitoring.</p>
            </div>
          ) : (
            <div className="flex items-start gap-2 p-2 rounded-lg bg-red-50">
              <AlertTriangle size={14} className="text-red-600 shrink-0 mt-0.5" />
              <p className="text-xs text-red-700">Higher risk. Recommend starter product with lower limits and review period.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
